
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import DiscordSVG from '@/assets/DiscordSVG.vue';
import RewardButton from './RewardButton.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { functions } from '@/firebase';
import _get from 'lodash/get';
import { redirectToLinkDiscord } from '@/components/JoinOurDiscord/utils';
import { DISCORD_INVITE_LINK } from '@/utils';

export default Vue.extend({
	name: 'SetUpSection',
	components: {
		DiscordSVG,
		RewardButton,
	},
	data() {
		return {
			buttonStyles: {
				width: '100%',
				padding: '14px',
				borderRadius: 'var(--global-border-radius-md)',
			},
			isCheckingStatus: false,
			isFreemiumInfoIssue: false,
			invalidatedOtherUsers: false,
			emailValidationError: '',
			discordError: '',
		};
	},
	computed: {
		...mapState([
			'authUser',
			'user',
		]),
		isEmailValidated(): boolean {
			return !!(
				this.authUser &&
				this.authUser.emailVerified &&
				!_get(this.user, 'freemiumInfo.invalidEmail') &&
				!(
					this.isFreemiumInfoIssue &&
					_get(this.user, 'freemiumInfo.noFingerprint')
				)
			);
		},
		isDiscordLinked(): boolean {
			return !!(
				this.isEmailValidated &&
				_get(this.user, 'discordUserId') &&
				!_get(this.user, 'freemiumInfo.noDiscord')
			);
		},
		isDiscordMembershipFound(): boolean {
			return !!(
				this.isEmailValidated &&
				_get(this.user, 'discordUserId') &&
				!_get(this.user, 'freemiumInfo.noDiscord') &&
				!_get(this.user, 'freemiumInfo.noDiscordMemberRd') &&
				_get(this.user, 'freemiumInfo.runnitFreemiumPassportId')
			);
		},
		isSetupCompleted(): boolean {
			return !!(
				this.isEmailValidated &&
				this.isDiscordLinked &&
				this.isDiscordMembershipFound
			);
		},
		getDiscordButtonText(): string {
			if (this.isDiscordLinked && this.isDiscordMembershipFound) {
				return 'Discord connected';
			}
			if (this.isDiscordLinked && !this.isDiscordMembershipFound) {
				return this.isCheckingStatus ? 'Loading...' : 'Confirm Discord membership';
			}
			return 'Link your Discord';
		},
	},
	watch: {
		isEmailValidated(newValue) {
			if (newValue && this.emailValidationError) {
				this.emailValidationError = '';
			}
		},
		isDiscordLinked(newValue) {
			if (newValue && this.discordError) {
				this.discordError = '';
			}
		}
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'setUpsellDialog',
			'routerPush',
		]),

		// Helper method to safely get nested properties
		getNestedValue(obj: any, path: string, defaultValue: any = undefined) {
			return _get(obj, path, defaultValue);
		},

		validateEmail() {
			if (this.isEmailValidated) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Your email is already validated!',
					show: true,
				});
				return;
			}

			// If email is not verified, show an error message
			if (this.authUser && !this.authUser.emailVerified) {
				this.emailValidationError = 'Please check your inbox and verify your email.';

				// Send verification email if needed
				this.authUser.sendEmailVerification().then(() => {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.INFO,
						message: 'Verification email sent. Please check your inbox.',
						show: true,
					});
				}).catch((error) => {
					console.error('Error sending verification email:', error);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Error sending verification email. Please try again later.',
						show: true,
					});
				});

				return;
			}

			// If there's an issue with the email
			if (_get(this.user, 'freemiumInfo.invalidEmail')) {
				this.emailValidationError = 'We couldn\'t confirm that your email address is a valid inbox (e.g. using a disposable email service).';
				return;
			}

			if (this.isFreemiumInfoIssue && _get(this.user, 'freemiumInfo.noFingerprint')) {
				this.emailValidationError = 'We couldn\'t verify your account. Please try connecting your Discord first.';
				return;
			}

			this.updateSnackbar({
				status: SNACKBAR_STATUS.INFO,
				message: 'Checking email validation status...',
				show: true,
			});

			// Trigger check for valid email
			this.checkAccountStatus();
		},

		handleDiscordAction() {
			if (!this.isEmailValidated) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.INFO,
					message: 'Please validate your email first.',
					show: true,
				});
				return;
			}

			if (this.isDiscordLinked && this.isDiscordMembershipFound) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Your Discord is already connected and you\'ve joined our server!',
					show: true,
				});
				return;
			}

			// If we're confirming Discord membership
			if (this.isDiscordLinked && !this.isDiscordMembershipFound) {
				this.verifyDiscordServerMembership();
				return;
			}

			// First time clicking - initiate Discord connection
			if (!_get(this.user, 'discordUserId')) {
				// Begin Discord linking process
				this.linkDiscord();
				return;
			}
		},

		linkDiscord() {
			redirectToLinkDiscord();
		},

		goToServerInviteScreen () {
			window.open(DISCORD_INVITE_LINK, '_blank').focus();
		},

		async verifyDiscordServerMembership() {
			if (this.isCheckingStatus) return;

			this.isCheckingStatus = true;
			this.discordError = '';

			try {
				const functionRef = functions.httpsCallable('unlockRunnitsFreemium');
				const { data } = await functionRef();

				const { success, isFreemiumInfoIssue } = data;
				this.isFreemiumInfoIssue = !!isFreemiumInfoIssue;

				if (!success && isFreemiumInfoIssue) {
					if (_get(this.user, 'freemiumInfo.noDiscord')) {
						this.discordError = 'We couldn\'t verify your Discord account connection. Please try connecting again.';
					} else if (_get(this.user, 'freemiumInfo.noDiscordMemberRd')) {
						this.discordError = 'We checked, and found that you have not joined our Discord server! Please join using the button below, then try again.';
					} else {
						this.discordError = 'There was an issue with your account. Please try again or contact support.';
					}

					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Verification not successful. Please check the error message.',
						show: true,
					});
				} else if (success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: 'Success! Your Discord is connected and you\'ve joined our server.',
						show: true,
					});
				} else {
					this.discordError = 'There was an unexpected error. Please try again later.';
					console.error('Unexpected response from unlockRunnitsFreemium:', data);
				}
			} catch (error) {
				console.error('Error verifying Discord membership:', error);
				this.discordError = 'There was an error checking your Discord status. Please try again later.';
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error checking Discord status. Please try again later.',
					show: true,
				});
			} finally {
				this.isCheckingStatus = false;
			}
		},

		async checkAccountStatus() {
			if (this.isCheckingStatus) return;

			this.isCheckingStatus = true;
			this.emailValidationError = '';

			try {
				const functionRef = functions.httpsCallable('unlockRunnitsFreemium');
				const { data } = await functionRef();

				const { success, isFreemiumInfoIssue } = data;
				this.isFreemiumInfoIssue = !!isFreemiumInfoIssue;

				if (!success && isFreemiumInfoIssue) {
					if (_get(this.user, 'freemiumInfo.invalidEmail')) {
						this.emailValidationError = 'We couldn\'t confirm that your email address is a valid inbox (e.g. using a disposable email service).';
					} else if (_get(this.user, 'freemiumInfo.noFingerprint')) {
						this.emailValidationError = 'We couldn\'t verify your account. Please try connecting your Discord first.';
					} else {
						this.emailValidationError = 'There was an issue with your account. Please try again or contact support.';
					}
				} else if (success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: 'Email validated successfully!',
						show: true,
					});
				}
			} catch (error) {
				console.error('Error checking account status:', error);
				this.emailValidationError = 'There was an error checking your account status. Please try again later.';
			} finally {
				this.isCheckingStatus = false;
			}
		},
	},
	mounted() {
		// Check initial status when component mounts
		this.$nextTick(() => {
			// Allow component to fully render before checking status
			if (this.authUser && this.user) {
				// Reset any existing errors
				this.emailValidationError = '';
				this.discordError = '';
			}
		});
	}
});
